import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { platformFetch } from "@/services/http/platform-fetch";

/** One row of `GET /api/platform/admin/tenants/:tenantId/admins` (S11). */
export type SchoolAdmin = {
  userId: string;
  email: string;
  name: string | null;
  hasPassword: boolean;
  firstSignedInAt: string | null;
  invite: { kind: string };
  canCorrectEmail: boolean;
};

export const schoolAdminsQueryKey = (tenantId: string) => ["platform-school-admins", tenantId];

async function fetchAdmins(tenantId: string): Promise<SchoolAdmin[]> {
  const res = await platformFetch(`/platform/admin/tenants/${tenantId}/admins`);
  if (!res.ok) throw await readApiErrorResponse(res);
  return ((await res.json()) as { data: { admins: SchoolAdmin[] } }).data.admins;
}

const INPUT =
  "w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-1)] px-3 py-2 text-[var(--text-primary)]";
const BUTTON = "rounded-md border border-[var(--border-strong)] px-3 py-1 text-sm";

/**
 * MT-1 Plan 2 S15 (FR-54 UI): a school's admins — each with their state; invite another; Remove
 * after a confirmation that names the person; "Correct email" only while the admin has not
 * started (`canCorrectEmail`). Refusals (`LAST_ADMIN`, `ADMIN_ALREADY_ACTIVE`, `EMAIL_EXISTS`)
 * are shown from `platform.errors`, never the server's text.
 */
export function SchoolAdminsCard({
  tenantId,
  onChanged,
}: {
  tenantId: string;
  onChanged?: () => void;
}) {
  const t = useTranslations("platform.school.admins");
  const tRoot = useTranslations();
  const locale = useLocale();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: schoolAdminsQueryKey(tenantId),
    queryFn: () => fetchAdmins(tenantId),
  });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<SchoolAdmin | null>(null);
  const [correcting, setCorrecting] = useState<SchoolAdmin | null>(null);
  const [newEmail, setNewEmail] = useState("");

  const label = (a: SchoolAdmin) => a.name ?? a.email;
  const fail = (err: unknown) =>
    setError(
      apiErrorText(err, tRoot, "platform.school.admins.actionFailed", {
        namespace: "platform.errors",
      }),
    );
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: schoolAdminsQueryKey(tenantId) });
    onChanged?.();
  };

  async function send(path: string, method: string, body?: unknown) {
    const res = await platformFetch(path, {
      method,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    if (!res.ok) throw await readApiErrorResponse(res);
  }

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      await action();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  const invite = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await send(`/platform/admin/tenants/${tenantId}/admins`, "POST", {
        email,
        name,
        locale: locale === "hi" ? "hi" : "en",
      });
      setEmail("");
      setName("");
      setMessage(t("invited"));
      refresh();
    });
  };

  const remove = (admin: SchoolAdmin) =>
    void run(async () => {
      setConfirming(null);
      await send(`/platform/admin/tenants/${tenantId}/admins/${admin.userId}`, "DELETE");
      setMessage(t("removed", { name: label(admin) }));
      refresh();
    });

  const correct = (e: React.FormEvent) => {
    e.preventDefault();
    const admin = correcting;
    if (!admin) return;
    void run(async () => {
      await send(`/platform/admin/tenants/${tenantId}/admins/${admin.userId}`, "PATCH", {
        email: newEmail,
      });
      setCorrecting(null);
      setNewEmail("");
      setMessage(t("emailCorrected"));
      refresh();
    });
  };

  function state(a: SchoolAdmin): string {
    if (a.firstSignedInAt) return t("state.active");
    if (a.hasPassword) return t("state.passwordSet");
    return t("state.invited");
  }

  return (
    <section
      className="mt-6 rounded-lg border border-[var(--border-strong)] p-4"
      aria-labelledby="school-admins-title"
      data-testid="school-admins-card"
    >
      <h2 id="school-admins-title" className="text-lg font-semibold">
        {t("title")}
      </h2>
      {query.isError && (
        <p role="alert" className="mt-2 text-sm text-[var(--danger)]">
          {apiErrorText(query.error, tRoot, "platform.school.admins.loadFailed", {
            namespace: "platform.errors",
          })}
        </p>
      )}
      {query.data && (
        <ul aria-labelledby="school-admins-title" className="mt-2 space-y-2">
          {query.data.map((admin) => (
            <li
              key={admin.userId}
              className="rounded-md border border-[var(--border-strong)] px-3 py-2 text-sm"
              data-testid="school-admin-row"
            >
              <p>
                <span className="font-medium">{admin.name ?? admin.email}</span>
                {admin.name ? ` (${admin.email})` : ""}: {state(admin)}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {admin.canCorrectEmail && (
                  <button
                    type="button"
                    className={BUTTON}
                    onClick={() => {
                      setCorrecting(admin);
                      setNewEmail("");
                    }}
                  >
                    {t("correct", { name: label(admin) })}
                  </button>
                )}
                <button
                  type="button"
                  className={BUTTON}
                  disabled={busy}
                  onClick={() => setConfirming(admin)}
                >
                  {t("remove", { name: label(admin) })}
                </button>
              </div>
              {confirming?.userId === admin.userId && (
                <div
                  role="alertdialog"
                  aria-labelledby={`remove-admin-${admin.userId}`}
                  className="mt-2 space-y-2 rounded-md border border-[var(--border-strong)] p-3"
                >
                  <p id={`remove-admin-${admin.userId}`}>
                    {t("confirmText", { name: label(admin) })}
                  </p>
                  <div className="flex gap-2">
                    <button type="button" className={BUTTON} onClick={() => remove(admin)}>
                      {t("confirmYes")}
                    </button>
                    <button type="button" className={BUTTON} onClick={() => setConfirming(null)}>
                      {t("confirmNo")}
                    </button>
                  </div>
                </div>
              )}
              {correcting?.userId === admin.userId && (
                <form onSubmit={correct} className="mt-2 space-y-2">
                  <label htmlFor={`correct-email-${admin.userId}`} className="block">
                    {t("newEmailLabel")}
                  </label>
                  <input
                    id={`correct-email-${admin.userId}`}
                    type="email"
                    required
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className={INPUT}
                  />
                  <div className="flex gap-2">
                    <button type="submit" className={BUTTON} disabled={busy}>
                      {t("saveEmail")}
                    </button>
                    <button type="button" className={BUTTON} onClick={() => setCorrecting(null)}>
                      {t("cancel")}
                    </button>
                  </div>
                </form>
              )}
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={invite} className="mt-4 space-y-2" data-testid="school-admin-invite">
        <h3 className="font-medium">{t("inviteTitle")}</h3>
        <label htmlFor="school-admin-email" className="block text-sm">
          {t("emailLabel")}
        </label>
        <input
          id="school-admin-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={INPUT}
        />
        <label htmlFor="school-admin-name" className="block text-sm">
          {t("nameLabel")}
        </label>
        <input
          id="school-admin-name"
          required
          maxLength={120}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={INPUT}
        />
        <button type="submit" className={BUTTON} disabled={busy}>
          {busy ? t("inviting") : t("invite")}
        </button>
      </form>
      <div aria-live="polite">{message && <p className="mt-2 text-sm">{message}</p>}</div>
      {error && (
        <p role="alert" className="mt-2 text-sm text-[var(--danger)]">
          {error}
        </p>
      )}
    </section>
  );
}
